import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Labels } from '@core/constants/labels';
import { Message } from '@core/models/message.model';
import { environment } from '@environment/environment';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, catchError, throwError } from 'rxjs';
import { AuthService } from '../auth-service/auth-service.service';

@Injectable({
  providedIn: 'root',
})
export class AnnotationManagerService {
  fromOSD: BehaviorSubject<any[]>;
  toOSD: BehaviorSubject<any[]>;
  toOSDTemp: BehaviorSubject<any[]>;
  focusOn: BehaviorSubject<any>;
  isLoading: BehaviorSubject<{ id: number; loading: boolean }>;

  labels = Labels.ANNOTATION_CONTROL;

  constructor(
    private httpClient: HttpClient,
    private toastrService: ToastrService,
    private authService: AuthService,
  ) {
    this.fromOSD = new BehaviorSubject<any[]>([]);
    this.toOSD = new BehaviorSubject<any[]>([]);
    this.focusOn = new BehaviorSubject<any>(null);
    this.toOSDTemp = new BehaviorSubject<any[]>([]);
    this.isLoading = new BehaviorSubject<{ id: number; loading: boolean }>({
      id: -1,
      loading: false,
    });
  }

  setAnnotationsLoading(image_id: number) {
    if (this.authService.isAnonymousUser) {
      // Anonymous annotations are not saved
      this.isLoading.next({ id: image_id, loading: false });
    } else {
      this.isLoading.next({ id: image_id, loading: true });
    }
  }

  setAnnotationsLoaded(image_id: number) {
    this.isLoading.next({ id: image_id, loading: false });
  }

  syncAnnotations(data: any[], imageId: number, callback: any) {
    const route = `${environment.examInfoBackend}/annotation/for_image/${imageId}/batch`;

    this.httpClient.post<any[]>(route, data).subscribe((data) => {
      callback(data);
    });
  }

  getAnnotations(imageId: number, callback: any) {
    const route = `${environment.examInfoBackend}/annotation/for_image/${imageId}`;

    this.httpClient.get(route).subscribe((data) => {
      callback(data);
    });
  }

  turnToMessage(annotationId: number, examId: number): Observable<Message> {
    const route = `${environment.examInfoBackend}/message/for_exam/${examId}/for_annotation/${annotationId}`;
    return this.httpClient.post<Message>(route, {});
  }

  downloadAnnotationImage(imageId: number, callback: any) {
    const route = `${environment.examInfoBackend}/annotation/for_image/${imageId}/export_image`;
    this.httpClient
      .get(route, { responseType: 'blob' })
      .pipe(catchError((error) => throwError(() => error)))
      .subscribe(
        (response: any) => {
          const dataType = response.type;
          const binaryData = [];
          binaryData.push(response);
          const downloadLink = document.createElement('a');
          downloadLink.href = window.URL.createObjectURL(
            new Blob(binaryData, { type: dataType }),
          );
          const filename = `annotations_for_${imageId}`;
          if (filename) downloadLink.setAttribute('download', filename);
          document.body.appendChild(downloadLink);
          downloadLink.click();
          callback();
        },
        (err) => {
          this.toastrService.error(this.labels.ERROR_NO_MEASURE_ANNOTATIONS);
          callback();
        },
      );
  }

  revokeExternalUserAuthorization(
    authorizationId: number,
    unrevoke: boolean = false,
  ) {
    const route = `${environment.examInfoBackend}/external_user_authorization/${authorizationId}/revoke`;
    return this.httpClient.post(route, { unrevoke });
  }
}
